import React from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Sparkles, ArrowRight, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useEntitlements } from "../contexts/EntitlementsContext";

export default function FeatureGate({ featureKey, children }) {
  const { entitled, features, loading } = useEntitlements();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="h-[60vh] grid place-items-center text-muted-foreground font-mono text-sm">
        Checking your plan…
      </div>
    );
  }
  if (entitled(featureKey)) return children;

  const f = features.find((x) => x.key === featureKey) || {};
  const inControl = f.tiers?.control && !f.tiers?.growth;

  return (
    <div className="p-6 lg:p-10 max-w-2xl mx-auto" data-testid="feature-gate">
      <div className="card-flat p-8 text-center space-y-5">
        <div className="h-16 w-16 mx-auto rounded-full bg-accent/10 border border-accent/20 grid place-items-center">
          <Lock className="h-7 w-7 text-accent" />
        </div>
        <div>
          <div className="label-eyebrow mb-1">Premium feature</div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="gate-feature-name">
            {f.name || "This feature"} is not in your plan
          </h1>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">          {f.description || "This module requires a higher plan or an add-on."}{" "}
          {inControl && <span className="text-foreground">It's included in <strong>Lestari Control</strong>.</span>}{" "}
          {f.addon_price_sgd ? (
            <span>You can unlock it now as an add-on for <strong className="text-accent">S${f.addon_price_sgd}/month</strong>.</span>
          ) : null}
        </p>
        {featureKey === "carbon_tax" && (
          <p className="text-[11px] text-muted-foreground max-w-md mx-auto" data-testid="icp-plan-note">
            Summary figures (carbon tax, internal carbon charge, offsets) are already included free on the Finance &amp; Offset tab — this full ICP &amp; Offset workspace is the plan add-on.
          </p>
        )}
        {!!(f.addon_price_sgd) && (
          <Badge variant="outline" className="text-xs"><Sparkles className="h-3 w-3 mr-1 text-accent" /> Add-on available · S${f.addon_price_sgd}/mo</Badge>
        )}
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button variant="primary" onClick={() => navigate("/billing?tab=marketplace")} data-testid="gate-upgrade-btn">
            <Sparkles className="h-4 w-4" /> {f.addon_price_sgd ? `Add for S$${f.addon_price_sgd}/mo` : "Upgrade plan"} <ArrowRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={() => navigate("/billing")} data-testid="gate-billing-btn">
            <ShieldCheck className="h-4 w-4" /> View billing & plan
          </Button>
        </div>
      </div>
    </div>
  );
}
